import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { wrapApiCall } from '@/lib/axios'
import {
  BulkReceivePayload,
  BulkReceiveResult,
  CategoryPayload,
  CreateProductDto,
  CreateProductSalePayload,
  PageResult,
  ProductCategoryEntity,
  ProductListQuery,
  ProductRowEntity,
  ProductSale,
  ProductSalesQuery,
  ProductSalesResult,
  ReorderLevelPayload,
  StockMovementEntity,
  StockMovementPayload,
  StockMovementQuery,
  StockSummaryEntity,
  UpdateProductDto,
} from './model'

const normalizePage = <T>(response: {
  data?: T[]
  pagination?: Partial<PageResult<T>['pagination']>
}): PageResult<T> => {
  const page = response.pagination?.page ?? 1
  const pageSize = response.pagination?.pageSize ?? 20
  const total = response.pagination?.total ?? response.data?.length ?? 0
  return {
    data: response.data ?? [],
    pagination: {
      page,
      pageSize,
      total,
      totalPages: response.pagination?.totalPages ?? Math.max(1, Math.ceil(total / pageSize)),
    },
  }
}

export const ProductService = (client: AxiosInstance) => {
  return {
    async getProducts(opt: ProductListQuery): Promise<PageResult<ProductRowEntity>> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination?: PageResult<ProductRowEntity>['pagination']
          data?: Record<string, unknown>[]
        }>('/v2/products', { params: opt })
      )
      const page = normalizePage(data)
      return {
        ...page,
        data: page.data.map((item) => deserialize(item, ProductRowEntity)),
      }
    },

    async getProductById(id: string): Promise<ProductRowEntity> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/v2/products/${id}`)
      )
      return deserialize(data, ProductRowEntity)
    },

    async createProduct(payload: CreateProductDto): Promise<ProductRowEntity> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/v2/products', payload)
      )
      return deserialize(data, ProductRowEntity)
    },

    async updateProduct(id: string, payload: UpdateProductDto): Promise<ProductRowEntity> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/v2/products/${id}`, payload)
      )
      return deserialize(data, ProductRowEntity)
    },

    async deleteProduct(id: string): Promise<void> {
      await wrapApiCall(() => client.delete(`/v2/products/${id}`))
    },

    async getCategories(): Promise<ProductCategoryEntity[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>('/v2/product-categories')
      )
      return (data ?? []).map((item) => deserialize(item, ProductCategoryEntity))
    },

    async createCategory(payload: CategoryPayload): Promise<ProductCategoryEntity> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/v2/product-categories', payload)
      )
      return deserialize(data, ProductCategoryEntity)
    },

    async updateCategory(id: string, payload: CategoryPayload): Promise<ProductCategoryEntity> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/v2/product-categories/${id}`, payload)
      )
      return deserialize(data, ProductCategoryEntity)
    },

    async deleteCategory(id: string): Promise<void> {
      await wrapApiCall(() => client.delete(`/v2/product-categories/${id}`))
    },

    async getStockSummary(productId: string, branchId: number): Promise<StockSummaryEntity> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/v2/stock/products/${productId}/summary`, {
          params: { branchId },
        })
      )
      return deserialize(data, StockSummaryEntity)
    },

    async getStockMovements(query: StockMovementQuery): Promise<PageResult<StockMovementEntity>> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination?: PageResult<StockMovementEntity>['pagination']
          data?: Record<string, unknown>[]
        }>('/v2/stock/movements', { params: query })
      )
      const page = normalizePage(data)
      return {
        ...page,
        data: page.data.map((item) => deserialize(item, StockMovementEntity)),
      }
    },

    async createStockMovement(payload: StockMovementPayload): Promise<void> {
      await wrapApiCall(() => client.post('/v2/stock/movements', payload))
    },

    async bulkReceiveStock(payload: BulkReceivePayload): Promise<BulkReceiveResult> {
      const { data } = await wrapApiCall(() =>
        client.post<BulkReceiveResult>('/v2/stock/movements/bulk-receive', payload)
      )
      return data
    },

    async setReorderLevel(payload: ReorderLevelPayload): Promise<void> {
      await wrapApiCall(() => client.post('/v2/stock/reorder-level', payload))
    },

    async createProductSale(payload: CreateProductSalePayload): Promise<ProductSale> {
      const { branchId, ...purchase } = payload
      void branchId
      const { data } = await wrapApiCall(() =>
        client.post<{
          purchaseId: string
          receiptId: number
          receiptCode: string
          subtotal: number
          discount: number
          total: number
          paid?: number
          change?: number
        }>('/product-purchases', purchase)
      )
      return {
        id: data.purchaseId,
        receiptId: data.receiptId,
        receiptCode: data.receiptCode,
        soldAt: new Date().toISOString(),
        branchId: payload.branchId,
        branchName: '',
        buyer: payload.memberId ? { id: payload.memberId, name: '' } : undefined,
        items: [],
        paymentType: payload.paymentType,
        receiptPaymentMethod: payload.receiptPaymentMethod,
        subtotal: data.subtotal,
        discount: data.discount,
        vat: 0,
        total: data.total,
        paid: data.paid,
        change: data.change,
        status: 'COMPLETED',
        voidEligibleUntil: '',
        canVoid: false,
      }
    },

    async getProductSales(query: ProductSalesQuery): Promise<ProductSalesResult> {
      const { branchId, status, ...params } = query
      void branchId
      const { data } = await wrapApiCall(() =>
        client.get<ProductSalesResult>('/product-purchases', {
          params: {
            ...params,
            status: status === 'COMPLETED' ? 'PAID' : status === 'VOIDED' ? 'VOID' : undefined,
          },
        })
      )
      const page = normalizePage(data)
      return {
        ...page,
        serverTime: data.serverTime,
        data: page.data.map((sale) => ({
          ...sale,
          status: (sale.status as unknown as string) === 'PAID' ? 'COMPLETED' : 'VOIDED',
          vat: sale.vat ?? 0,
          items: sale.items.map((item) => ({
            ...item,
            price: item.price ?? (item as unknown as { unitPrice: number }).unitPrice,
            total: item.total ?? (item as unknown as { lineTotal: number }).lineTotal,
            discount:
              item.discount ?? (item as unknown as { lineDiscount?: number }).lineDiscount ?? 0,
            trackStock: item.trackStock ?? false,
          })),
        })),
      }
    },

    async voidProductSale(id: string, reason: string): Promise<void> {
      await wrapApiCall(() => client.post(`/product-purchases/${id}/void`, { reason }))
    },
  }
}
